import { useState } from 'react';
import { Buildings, EnvelopeSimple, MapPin, Phone, User } from '@phosphor-icons/react';
import { Auswahl, FeldRaster, ImmoButton, LivePreviewCard, SegmentedControl, StepForm, TextFeld } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { createLink } from '../../../../../lib/entityLinks';
import { initialen } from '../../../../../lib/immobilien/format';
import {
  ANREDEN, KONTAKTARTEN, LAENDER, UNTERNEHMENSARTEN, kontaktFehler, kontaktPayload, leererKontakt, type KontaktArt, type KontaktEntwurf,
} from '../../../../../lib/immobilien/kontaktFormular';

const opt = (l: readonly string[]) => l.map(x => ({ key: x, label: x }));

/**
 * „Neuer Kontakt“ im neuen Design – Felder und Speichern wie der bisherige Kontakt-Dialog:
 * Kontakt in kontakte anlegen und mit dem Objekt verknüpfen (Rolle = Kontakt- bzw. Unternehmensart).
 */
export function NeuerKontaktFormular({ objektId, onAbbrechen, onAngelegt }: {
  objektId: string;
  onAbbrechen: () => void;
  onAngelegt: (meldung: string, fehler?: boolean) => void;
}) {
  const [art, setArt] = useState<KontaktArt>('privatperson');
  const [k, setK] = useState<KontaktEntwurf>(leererKontakt);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const set = (f: keyof KontaktEntwurf) => (v: string) => setK(x => ({ ...x, [f]: v }));

  const pflicht = kontaktFehler(art, k);
  const person = art === 'privatperson';
  const name = person ? [k.anrede, k.vorname, k.nachname].map(s => s.trim()).filter(Boolean).join(' ') : k.firmenname.trim();
  const rolle = person ? (k.kontaktart || 'Sonstige') : (k.unternehmensart || 'Sonstige');

  const speichere = async () => {
    if (pflicht) { setFehler(pflicht); return; }
    setSpeichert(true);
    setFehler(null);
    try {
      const payload = kontaktPayload(art, k);
      const { data, error } = await supabase.from('kontakte').insert(payload).select('id').single();
      if (error) throw new Error(error.message);
      const link = await createLink({ sourceType: 'objekt', sourceId: objektId, targetType: 'kontakt', targetId: (data as { id: string }).id, linkType: 'kontakt', rolle: payload.typ ?? 'Sonstige' });
      if (link.error) onAngelegt(`Kontakt angelegt, aber nicht verknüpft: ${link.error.message}`, true);
      else onAngelegt('Kontakt angelegt und verknüpft');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 800 }}>Neuer Kontakt</span>
        <SegmentedControl hoehe={34} ariaLabel="Art des Kontakts" wert={art} onChange={setArt}
          segmente={[{ key: 'privatperson', label: 'Privatperson', icon: User }, { key: 'unternehmen', label: 'Unternehmen', icon: Buildings }]} />
      </div>
      {fehler && <span role="alert" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}
      <StepForm
        hinweis={pflicht ? `Noch offen: ${person ? 'Nachname' : 'Firmenname'}` : 'Alles bereit – Kontakt kann angelegt werden'}
        bereit={!pflicht}
        speichert={speichert}
        onSpeichern={() => { void speichere(); }}
        speichernLabel="Kontakt anlegen"
        sekundaer={<ImmoButton art="fuellung" groesse={48} pille={false} onClick={onAbbrechen}>Abbrechen</ImmoButton>}
        schritte={[
          {
            titel: person ? 'Person' : 'Unternehmen', untertitel: person ? 'Name und Kontaktart' : 'Firma und Unternehmensart', erledigt: !pflicht,
            inhalt: person ? (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <Auswahl label="Anrede" wert={k.anrede || null} onChange={set('anrede')} optionen={opt(ANREDEN)} />
                <FeldRaster minBreite={170}>
                  <TextFeld label="Vorname" wert={k.vorname} onChange={set('vorname')} />
                  <TextFeld label="Nachname *" wert={k.nachname} onChange={set('nachname')} />
                  <TextFeld label="Geburtsdatum" type="date" wert={k.geburtsdatum} onChange={set('geburtsdatum')} />
                </FeldRaster>
                <Auswahl label="Kontaktart" wert={k.kontaktart || null} onChange={set('kontaktart')} optionen={opt(KONTAKTARTEN)} />
              </div>
            ) : (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <TextFeld label="Firmenname *" wert={k.firmenname} onChange={set('firmenname')} />
                <Auswahl label="Unternehmensart" wert={k.unternehmensart || null} onChange={set('unternehmensart')} optionen={opt(UNTERNEHMENSARTEN)} />
                <Auswahl label="Anrede Ansprechpartner (optional)" wert={k.anrede || null} onChange={set('anrede')} optionen={opt(ANREDEN)} />
              </div>
            ),
          },
          {
            titel: 'Adresse', untertitel: 'optional', erledigt: !!(k.strasse.trim() || k.stadt.trim()),
            inhalt: (
              <div className="flex flex-col" style={{ gap: 12 }}>
                <FeldRaster minBreite={150}>
                  <TextFeld label="Straße" wert={k.strasse} onChange={set('strasse')} />
                  <TextFeld label="Hausnummer" wert={k.hausnummer} onChange={set('hausnummer')} />
                  <TextFeld label="PLZ" wert={k.plz} onChange={set('plz')} inputMode="numeric" />
                  <TextFeld label="Stadt" wert={k.stadt} onChange={set('stadt')} />
                </FeldRaster>
                <Auswahl label="Land" wert={k.land || null} onChange={set('land')} optionen={opt(LAENDER)} />
              </div>
            ),
          },
          {
            titel: 'Kontaktmöglichkeiten', untertitel: 'E-Mail und Telefon', erledigt: !!(k.email.trim() || k.festnetz.trim() || k.handynummer.trim()),
            inhalt: (
              <FeldRaster minBreite={170}>
                <TextFeld label="E-Mail" type="email" wert={k.email} onChange={set('email')} />
                <TextFeld label="Festnetz" type="tel" wert={k.festnetz} onChange={set('festnetz')} />
                <TextFeld label="Handynummer" type="tel" wert={k.handynummer} onChange={set('handynummer')} />
              </FeldRaster>
            ),
          },
          {
            titel: 'Bank & Steuer', untertitel: 'optional · Kreditor- und Debitor-ID werden automatisch vergeben', erledigt: !!(k.iban.trim() || k.steuernummer.trim()),
            inhalt: (
              <FeldRaster minBreite={170}>
                <TextFeld label="Kontoinhaber" wert={k.kontoinhaber} onChange={set('kontoinhaber')} />
                <TextFeld label="Bank" wert={k.bank} onChange={set('bank')} />
                <TextFeld label="IBAN" wert={k.iban} onChange={set('iban')} />
                <TextFeld label="BIC" wert={k.bic} onChange={set('bic')} />
                <TextFeld label="Steuernummer" wert={k.steuernummer} onChange={set('steuernummer')} />
                <TextFeld label="USt-IdNr." wert={k.ust_id_nr} onChange={set('ust_id_nr')} />
                <TextFeld label="Gläubiger-ID" wert={k.glaeubiger_id} onChange={set('glaeubiger_id')} />
              </FeldRaster>
            ),
          },
        ]}
        vorschau={
          <LivePreviewCard>
            <div className="flex items-center min-w-0" style={{ gap: 12 }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 15, fontWeight: 600 }}>
                {name ? initialen(name) : person ? <User size={20} /> : <Buildings size={20} />}
              </span>
              <div className="flex flex-col min-w-0">
                <span className="truncate" style={{ fontSize: 15, fontWeight: 800 }}>{name || (person ? 'Neue Person' : 'Neues Unternehmen')}</span>
                <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{rolle} · wird mit dem Objekt verknüpft</span>
              </div>
            </div>
            <div className="flex flex-col" style={{ gap: 6, paddingTop: 12, borderTop: '1px solid var(--line)', fontSize: 12, color: 'var(--ink2)' }}>
              <span className="flex items-center" style={{ gap: 8 }}><EnvelopeSimple size={14} />{k.email.trim() || 'Keine E-Mail'}</span>
              <span className="flex items-center" style={{ gap: 8 }}><Phone size={14} />{k.handynummer.trim() || k.festnetz.trim() || 'Kein Telefon'}</span>
              <span className="flex items-center" style={{ gap: 8 }}><MapPin size={14} />{[[k.strasse, k.hausnummer].map(s => s.trim()).filter(Boolean).join(' '), [k.plz, k.stadt].map(s => s.trim()).filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'Keine Adresse'}</span>
            </div>
          </LivePreviewCard>
        }
      />
    </div>
  );
}
